import axios, { AxiosInstance, AxiosError } from 'axios';
import { AUTH_API_URL, SELLER_API_URL, ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '../config/constants';

class ApiClient {
  private authClient: AxiosInstance;
  private sellerClient: AxiosInstance;
  private isRefreshing = false;
  private refreshSubscribers: ((token: string) => void)[] = [];

  constructor() {
    // Auth API client (port 5002)
    this.authClient = axios.create({
      baseURL: AUTH_API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Seller API client (port 5001)
    this.sellerClient = axios.create({
      baseURL: SELLER_API_URL,
      timeout: 35000,
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
      },
    });

    // Request interceptor to add token
    [this.authClient, this.sellerClient].forEach(client => {
      client.interceptors.request.use(
        (config) => {
          const token = localStorage.getItem(ACCESS_TOKEN_KEY);
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
          return config;
        },
        (error) => Promise.reject(error)
      );

      // Response interceptor to handle token refresh
      client.interceptors.response.use(
        (response) => response,
        async (error: AxiosError) => {
          const originalRequest = error.config as any;

          // Don't intercept login/register requests
          if (originalRequest.url?.includes('/auth/login') || 
              originalRequest.url?.includes('/auth/register')) {
            return Promise.reject(error);
          }

          if (error.response?.status === 401 && !originalRequest._retry) {
            if (this.isRefreshing) {
              return new Promise((resolve) => {
                this.refreshSubscribers.push((token: string) => {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                  resolve(client(originalRequest));
                });
              });
            }

            originalRequest._retry = true;
            this.isRefreshing = true;

            try {
              const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
              if (!refreshToken) {
                throw new Error('No refresh token');
              }

              // Add timeout to prevent infinite waiting
              const refreshPromise = this.authClient.post('/auth/refresh', {
                refreshToken,
              });

              const timeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Token refresh timeout')), 10000)
              );

              const response = await Promise.race([refreshPromise, timeoutPromise]) as any;

              const { accessToken } = response.data;
              localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);

              this.refreshSubscribers.forEach((callback) => callback(accessToken));
              this.refreshSubscribers = [];

              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              return client(originalRequest);
            } catch (refreshError) {
              console.error('Token refresh failed:', refreshError);
              this.logout();
              // Only redirect if not already on login page
              if (!window.location.pathname.includes('/login')) {
                const currentPath = window.location.pathname + window.location.search;
                window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
              }
              return Promise.reject(refreshError);
            } finally {
              this.isRefreshing = false;
            }
          }

          return Promise.reject(error);
        }
      );
    });
  }

  getAuthClient() {
    return this.authClient;
  }

  getSellerClient() {
    return this.sellerClient;
  }

  logout() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem('taminzo_user_data');
    localStorage.removeItem('taminzo_permissions');
  }
}

export default new ApiClient();
